import { formatDateTime } from "@/utils/formatDateTime.js";
import {
  BLANK_DISPLAY,
  sanitizeDisplayValue,
} from "@/utils/displaySanitize.js";

export const USER_CHIP_FILTERS = [
  { id: "all", label: "All" },
  { id: "super_admin", label: "Super admins" },
  { id: "admin", label: "Admins" },
];

export function normalizeUserRole(role) {
  const value = String(role || "").trim().toUpperCase();
  if (value === "SUPER_ADMIN") return "SUPER_ADMIN";
  if (!value) return "ADMIN";
  return value;
}

export function formatUserRole(role) {
  const value = sanitizeDisplayValue(role, "");
  if (!value) return BLANK_DISPLAY;
  return value.replace(/_/g, " ");
}

export function deriveUserRoleDisplay(role) {
  if (role === "SUPER_ADMIN") {
    return { label: "Super Admin", tone: "super", accessLevel: "Full platform" };
  }
  if (role === "ADMIN") {
    return { label: "Admin", tone: "admin", accessLevel: "Organization access" };
  }
  return { label: formatUserRole(role), tone: "neutral", accessLevel: BLANK_DISPLAY };
}

export function getUserInitials(name, email) {
  const source = (sanitizeDisplayValue(name, "") || sanitizeDisplayValue(email, "") || "?").trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

export function enrichUser(user) {
  const userRole = normalizeUserRole(user.user_role);
  const role = deriveUserRoleDisplay(userRole);
  return {
    ...user,
    user_role: userRole,
    display: {
      role,
      email: sanitizeDisplayValue(user.user_email_id),
      joinedLabel: user.created_on ? formatDateTime(user.created_on) : BLANK_DISPLAY,
      initials: getUserInitials(user.user_name, user.user_email_id),
      isSuperAdmin: userRole === "SUPER_ADMIN",
      canRemove: userRole !== "SUPER_ADMIN",
    },
  };
}

export function matchesUserChipFilter(user, chipId) {
  const userRole = normalizeUserRole(user.user_role);
  switch (chipId) {
    case "super_admin":
      return userRole === "SUPER_ADMIN";
    case "admin":
      return userRole === "ADMIN";
    default:
      return true;
  }
}

export function canRemoveUser(
  targetUser,
  { viewerIsSuperAdmin = false, viewerUserId = null } = {},
) {
  if (!viewerIsSuperAdmin) return false;

  const targetId = String(targetUser?.user_id || "").trim();
  const viewerId = String(viewerUserId || "").trim();
  if (targetId && viewerId && targetId === viewerId) return true;

  const targetRole = normalizeUserRole(targetUser?.user_role);
  if (targetRole === "SUPER_ADMIN") return false;
  return true;
}

export function superAdminUserActionError() {
  return {
    status: "ERROR",
    message: "Only super admins can manage users",
  };
}

export function buildUserHeroMetrics(users = []) {
  const total = users.length;
  const superAdmins = users.filter(
    (u) => normalizeUserRole(u.user_role) === "SUPER_ADMIN",
  ).length;
  const admins = users.filter(
    (u) => normalizeUserRole(u.user_role) === "ADMIN",
  ).length;
  const accessCoverage =
    total > 0 ? Math.round(((superAdmins + admins) / total) * 100) : 0;

  return {
    totalUsers: total,
    superAdmins,
    admins,
    accessCoverage,
  };
}
